import { createContext, type RefObject, useCallback, useContext } from 'react'
import { track } from 'src/utils/tracker'

type CommentsScrollContextValue = {
  /** Section root — used by the comment count button to scroll the page. */
  sectionRef: RefObject<HTMLElement | null>
  /** messageId → element, populated by each CommentItem via a callback ref.
   * Used by useScrollToComment to find the parent of a reply. */
  commentRefs: Map<string, HTMLElement>
}

export const CommentsScrollContext =
  createContext<CommentsScrollContextValue | null>(null)

export function useCommentsScrollContext() {
  return useContext(CommentsScrollContext)
}

export function useCommentsSectionRef() {
  return useContext(CommentsScrollContext)?.sectionRef
}

export function useScrollToComments() {
  const ctx = useContext(CommentsScrollContext)
  return useCallback(() => {
    track('comment_count_click')
    ctx?.sectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [ctx])
}

/** Returns a callback ref for a CommentItem to register itself in the map. */
export function useRegisterCommentRef(id: string) {
  const ctx = useContext(CommentsScrollContext)
  return useCallback(
    (el: HTMLElement | null) => {
      if (!ctx) return
      if (el) ctx.commentRefs.set(id, el)
      else ctx.commentRefs.delete(id)
    },
    [ctx, id],
  )
}
